<script setup>
import { reactive, ref, computed } from "vue"
import HideColumnsButton from '@/Shared/AGGrid/HideColumnsButton'
import ExportCSVButton from '@/Shared/AGGrid/ExportCSVButton'

import { UserAddIcon } from "@heroicons/vue/outline"

const props = defineProps({
    items: {
        type: Array,
        default: () => ([]),
    },
	pagination: {
		type: Boolean,
		default: true,
	},
	columnDefs: {
		type: Array,
		default: () => ([
			{ 
				field: "name",
				headerName: "Name",
				flex: 2,
			},
			{ 
				field: "email", 
				flex: 5
			},
			{ 
				field: "phone", 
				flex: 3
			},
			{ 
				field: "position", 
				flex: 2
			},
			{ 
				field: "organisation_name", 
				flex: 2 
			},
			{ 
				field: "is_admin",
				flex: 2,
				hide: true
			},
			{
				field: "primary_phone_number",
				flex: 4,
				hide: true
			}
		]),
	},
})

const emit = defineEmits({
    'item-clicked': {
        type: Object,
        default: () => ({}),
    },
})

function rowClicked(event){
	emit('item-clicked', event.data)
}
const rowData = reactive(props.items)

const defaultColDef = {
	sortable: true,
	filter: true,
	flex: 1,
	dataTestid: 'column',
	resizable: true,
	show: true,
}

const localColumnDefs = reactive(props.columnDefs)
function toggleColumn(data){
	var col = localColumnDefs.find(x => x.field == data.field)
	col.hide = data.hide
}

function clickedGrid(event){
	savePageNumber()
}

function savePageNumber(event){
	if(gridApi.value) {
		localStorage.setItem('users-list-cur-page', gridApi.value.paginationGetCurrentPage())
	}
}

const gridApi = ref(null)
function onGridReady(params) {
	gridApi.value = params.api
}

function onFirstDataRendered(params) {
	if(localStorage.getItem('users-list-cur-page')){
		gridApi.value.paginationGoToPage(parseInt(localStorage.getItem('users-list-cur-page')))
	}
}

// we want to be able to disable pagination if the user checks the "show all" box
const enablePagination = ref(true)
const perPage = computed(() => {
	return enablePagination.value ? 10 : props.items.length +1
})
function togglePagination(){
	enablePagination.value = !enablePagination.value
	gridApi.value.paginationSetPageSize(perPage.value)
}
const gridClasses = computed(() => {
	return enablePagination.value ? '' : 'hide-pagination'
})
</script>

<template>
    <div class="flex flex-col h-full grow">
		<ag-grid-vue
			data-testid="users-list"
			class="ag-theme-alpine"
			:class="gridClasses"
			domLayout='autoHeight'
			:rowHeight="48"
			rowClass="users-list-row"
			:columnDefs="localColumnDefs"
			:rowData="rowData"
			:defaultColDef="defaultColDef"
			rowSelection="none"
			animateRows="true"
			@cell-clicked="rowClicked"
			:pagination="enablePagination"
			:paginationPageSize="perPage"
			@grid-ready="onGridReady"
			@click="clickedGrid"
			@first-data-rendered="onFirstDataRendered"
		>
 		</ag-grid-vue>
    </div>
		
	<div class="w-full flex justify-end pr-4 pt-1">
		<HideColumnsButton
			:columnDefs="localColumnDefs"
			@toggle-column="toggleColumn"
		/>

		<ExportCSVButton
			:grid-api="gridApi"
		/>

		<Button
			class="mb-4 ml-1 text-xs py-1 px-2 self-end"
			color="light-grey"
			@click="togglePagination"
		>
			Show {{enablePagination ? 'All' : 'Pages'}}
		</Button>
	</div>
</template>

<style>
.users-list-row .ag-cell {
	cursor: pointer;
	display: flex;
	align-items: center;
}
</style>